import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { EmptyState, LoadingSkeleton } from "@/components/ui-system";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const NA = <span className="text-slate-400" title="This courier can't serve this lane">N/A</span>;

const SUMMARY_LABELS = { coverage: "Total coverage", rate: "Total rate" };

/**
 * Rate-calculator bulk results: every uploaded row rated on its own, couriers side by side.
 * A courier that can't serve a lane shows N/A (never ₹0) and is left out of its coverage
 * count and total. White-label: only canonical courier labels are shown.
 *
 * props:
 *  - input: { payment_mode, order_value, rows: [{ row, pickup_pincode, drop_pincode, dead_weight_kg,
 *             length_cm, breadth_cm, height_cm }] }
 */
export function BulkRateResults({ input }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { lane_count, couriers, rows }
  const [error, setError] = useState(null);

  const fetchRates = useCallback(async (isCancelled = () => false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/orders/quote/packages/rates`, input);
      if (!isCancelled()) setResult(data);
    } catch (e) {
      if (!isCancelled()) {
        setResult(null);
        setError(errDetail(e, "Could not calculate rates"));
      }
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [input]);

  useEffect(() => {
    let cancelled = false;
    fetchRates(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchRates]);

  const couriers = useMemo(() => result?.couriers || [], [result]);
  const laneCount = result?.lane_count ?? 0;

  if (loading) {
    return (
      <div className="space-y-3" data-testid="rc-bulk-loading">
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking {input.rows.length} {input.rows.length === 1 ? "lane" : "lanes"} with every courier — this can take a
          minute.
        </p>
        <LoadingSkeleton variant="table" rows={5} cols={4} />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Couldn't calculate rates"
        description={error}
        action={
          <Button variant="outline" onClick={() => fetchRates()} data-testid="rc-bulk-retry">
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
        }
      />
    );
  }

  if (!result) return null;

  // One scroll box (both axes). The header row sticks to the top and the two summary rows
  // stick to the bottom (stacked: fixed 40px rows), so only the lane rows scroll between.
  const th = "sticky top-0 z-20 whitespace-nowrap border-b bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-700";
  const sumCell = "h-10 whitespace-nowrap border-t bg-slate-50 px-4 text-sm";

  return (
    <div className="space-y-2" data-testid="rc-bulk-results">
      <div className="max-h-[32rem] overflow-auto rounded-xl border bg-white" data-testid="rc-bulk-scroll">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={`${th} text-left`}>From pincode</th>
              <th className={`${th} text-left`}>To pincode</th>
              {couriers.map((c) => (
                <th key={c.key} className={`${th} text-right`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r, i) => (
              <tr key={r.row ?? i} className="hover:bg-slate-50">
                <td className="whitespace-nowrap border-b px-4 py-2 text-slate-700">{r.pickup_pincode}</td>
                <td className="whitespace-nowrap border-b px-4 py-2 text-slate-700">
                  {r.drop_pincode}
                  {r.error && <p className="text-xs text-red-600" data-testid={`rc-bulk-row-error-${r.row}`}>{r.error}</p>}
                </td>
                {couriers.map((c) => {
                  const rate = r.rates?.[c.key];
                  return (
                    <td key={c.key} className="whitespace-nowrap border-b px-4 py-2 text-right text-slate-700">
                      {rate != null ? <span className="tabular-nums">{inr(rate)}</span> : NA}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr data-testid="rc-bulk-total-coverage">
              <td className={`${sumCell} sticky bottom-10 z-10 font-bold text-slate-800`}>{SUMMARY_LABELS.coverage}</td>
              <td className={`${sumCell} sticky bottom-10 z-10`} />
              {couriers.map((c) => (
                <td key={c.key} className={`${sumCell} sticky bottom-10 z-10 text-right`}>
                  <span className="font-semibold tabular-nums text-slate-800">{c.served}/{laneCount}</span>
                </td>
              ))}
            </tr>
            <tr data-testid="rc-bulk-total-rate">
              <td className={`${sumCell} sticky bottom-0 z-10 font-bold text-slate-800`}>{SUMMARY_LABELS.rate}</td>
              <td className={`${sumCell} sticky bottom-0 z-10`} />
              {couriers.map((c) => (
                <td key={c.key} className={`${sumCell} sticky bottom-0 z-10 text-right`}>
                  {c.served ? <span className="font-bold tabular-nums text-slate-800">{inr(c.total)}</span> : NA}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        {laneCount} {laneCount === 1 ? "lane" : "lanes"} · scroll the table to see every lane and courier.
      </p>
    </div>
  );
}

export default BulkRateResults;
